import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { EntShell, Btn, Field, inputCls, Kicker, errText } from "@/components/enterprise/Ent";

const tokenKey = (code) => `wm_ent_${code}`;

const CodeEntry = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (code.trim()) navigate(`/e/${code.trim().toUpperCase()}`); }} className="max-w-sm mx-auto px-6 py-20" data-testid="join-code-form">
      <Kicker>Join a team event</Kicker>
      <h1 className="font-display text-4xl mt-3">Enter your event code.</h1>
      <div className="mt-6"><Field id="code" label="Event code"><input id="code" className={`${inputCls} uppercase tracking-[0.3em] text-xl`} maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} autoComplete="off" data-testid="join-code-input" /></Field></div>
      <Btn type="submit" className="w-full mt-4" data-testid="join-code-submit">Continue</Btn>
    </form>
  );
};

const Ratings = ({ items, phase, onDone, token, prompts }) => {
  const [r, setR] = useState({});
  const [refl, setRefl] = useState(["", ""]);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post(`/enterprise/participant/${token}/assessment`, { phase, ratings: r, reflections: phase === "post" ? refl : [] }); onDone(); }
    catch (e2) { toast.error(errText(e2)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="bg-white border border-[#ddd5c9] rounded-lg p-6 space-y-6" data-testid={`assessment-${phase}`}>
      <div>
        <h2 className="font-display text-2xl">{phase === "pre" ? "Before we begin" : "Before you go"}</h2>
        <p className="text-sm text-[#5a524b] mt-1">Optional and anonymous. Rate each statement from 1 (not yet) to 5 (very confident). Your name isn't attached to your answers.</p>
      </div>
      {items.map((it) => (
        <fieldset key={it.id}>
          <legend className="text-[15px] font-semibold">{it.statement}</legend>
          <div className="mt-2 flex gap-2" role="radiogroup" aria-label={it.label}>
            {[1, 2, 3, 4, 5].map((n) => (
              <label key={n} className={`w-11 h-11 flex items-center justify-center border rounded-md cursor-pointer font-semibold ${r[it.id] === n ? "bg-[#16130f] text-white border-[#16130f]" : "bg-white border-[#bdb3a6]"}`}>
                <input type="radio" className="sr-only" name={`${phase}-${it.id}`} value={n} checked={r[it.id] === n} onChange={() => setR({ ...r, [it.id]: n })} data-testid={`assess-${phase}-${it.id}-${n}`} />{n}
              </label>
            ))}
          </div>
        </fieldset>
      ))}
      {phase === "post" && prompts.map((p, i) => (
        <Field key={p} id={`refl-${i}`} label={`${p} (optional)`}>
          <textarea id={`refl-${i}`} rows={2} maxLength={400} className={inputCls} value={refl[i]} onChange={(e) => setRefl(refl.map((x, j) => (j === i ? e.target.value : x)))} data-testid={`assess-reflection-${i}`} />
        </Field>
      ))}
      <div className="flex gap-3 flex-wrap">
        <Btn type="submit" disabled={busy || Object.keys(r).length === 0} data-testid={`assess-${phase}-submit`}>Submit</Btn>
        <Btn type="button" variant="outline" onClick={onDone} data-testid={`assess-${phase}-skip`}>Skip</Btn>
      </div>
    </form>
  );
};

const Decision = ({ s, token, reload }) => {
  const [pick, setPick] = useState(s.team_answer_index);
  const closed = s.event_status === "completed";
  const save = async () => {
    try { await api.post(`/enterprise/participant/${token}/answer`, { answer_index: pick }); toast.success("Team decision saved. Anyone on your team can update it until the facilitator closes the event."); reload(); }
    catch (e) { toast.error(errText(e)); }
  };
  return (
    <section className="bg-white border border-[#ddd5c9] rounded-lg p-6" aria-labelledby="dec-h" data-testid="decision">
      <h2 id="dec-h" className="font-display text-2xl">Team decision</h2>
      <p className="mt-1 text-[15px] font-semibold">{s.challenge.question}</p>
      <div className="mt-4 space-y-2" role="radiogroup" aria-labelledby="dec-h">
        {s.challenge.options.map((o, i) => {
          const isSol = closed && s.solution?.answer_index === i;
          return (
            <label key={o} className={`flex gap-3 p-4 border rounded-md ${closed ? "" : "cursor-pointer"} ${isSol ? "border-[#24502a] bg-[#e7efe4]" : pick === i ? "border-[#16130f] bg-[#faf8f4]" : "border-[#ddd5c9]"}`}>
              <input type="radio" name="decision" disabled={closed} checked={pick === i} onChange={() => setPick(i)} className="mt-1 accent-[#7a1010]" data-testid={`decision-option-${i}`} />
              <span className="text-[15px]">{o}{isSol && <b className="block text-sm text-[#24502a] mt-1">Best-supported answer</b>}</span>
            </label>
          );
        })}
      </div>
      {s.team_answer_index !== null && !closed && <p className="mt-3 text-sm text-[#5a524b]" data-testid="decision-current">Your team's current decision: option {s.team_answer_index + 1}</p>}
      {!closed && <Btn className="mt-4" disabled={pick === null} onClick={save} data-testid="decision-submit">Submit team decision</Btn>}
      {closed && s.solution && <p className="mt-4 text-[15px] bg-[#f1ece4] p-4 rounded-md" data-testid="decision-explanation">{s.solution.explanation}</p>}
    </section>
  );
};

const Play = ({ token, onReset }) => {
  const [s, setS] = useState(null);
  const [skipped, setSkipped] = useState({});
  const load = useCallback(async () => {
    try { const r = await api.get(`/enterprise/participant/${token}`); setS(r.data); }
    catch { onReset(); }
  }, [token, onReset]);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  if (!s) return <div className="max-w-2xl mx-auto px-6 py-20" aria-busy="true">Loading…</div>;
  const accent = s.brand_hex || "#7a1010";
  const needPre = s.assessments_enabled && !s.assessments_done.includes("pre") && !skipped.pre && s.event_status !== "completed";
  const needPost = s.assessments_enabled && s.event_status === "completed" && !s.assessments_done.includes("post") && !skipped.post;

  return (
    <div className="max-w-2xl mx-auto px-5 py-10 space-y-6" data-testid="participant-view">
      <header className="border-l-4 pl-4" style={{ borderColor: accent }}>
        <div className="text-xs font-semibold tracking-[0.18em] uppercase" style={{ color: accent }}>{s.org_name}</div>
        <h1 className="font-display text-3xl sm:text-4xl mt-1">{s.program_name}</h1>
        <p className="text-sm text-[#5a524b] mt-1" data-testid="participant-team">{s.nickname} · {s.team_name}</p>
      </header>
      {needPre ? <Ratings items={s.assessment_items} phase="pre" token={token} prompts={s.reflection_prompts} onDone={() => { setSkipped({ ...skipped, pre: true }); load(); }} /> : (
        <>
          <section className="bg-white border border-[#ddd5c9] rounded-lg p-6" aria-labelledby="sc-h">
            <h2 id="sc-h" className="font-display text-2xl">The situation</h2>
            <p className="mt-2 text-[15px] leading-relaxed text-[#3b3530]">{s.scenario}</p>
          </section>
          <section className="rounded-lg p-6 text-white" style={{ backgroundColor: "#16130f" }} aria-labelledby="role-h" data-testid="role-card">
            <div className="text-xs font-semibold tracking-[0.18em] uppercase text-[#e9c98a]">Your role · private</div>
            <h2 id="role-h" className="font-display text-3xl mt-1">{s.role.title}{s.role.character ? `: ${s.role.character}` : ""}</h2>
            <ul className="mt-4 space-y-3">{s.role.clues.map((c, i) => <li key={c} className="flex gap-3 text-[15px]" data-testid={`role-clue-${i}`}><span className="font-display text-[#e9c98a]">{i + 1}</span>{c}</li>)}</ul>
            <p className="mt-5 text-sm text-[#d9d2c8]">Share these clues with your team out loud or in chat. Don't show your screen.</p>
            {s.hint && <p className="mt-3 text-sm text-[#e9c98a]" data-testid="role-hint">Hint: {s.hint}</p>}
          </section>
          <Decision s={s} token={token} reload={load} />
          {needPost && <Ratings items={s.assessment_items} phase="post" token={token} prompts={s.reflection_prompts} onDone={() => { setSkipped({ ...skipped, post: true }); load(); }} />}
          {s.event_status === "completed" && !needPost && <p className="text-center font-display text-2xl" data-testid="participant-done">Thank you for taking part.</p>}
          <button onClick={load} className="mx-auto flex items-center gap-2 text-sm underline" data-testid="participant-refresh"><RefreshCw size={14} aria-hidden="true" /> Refresh</button>
        </>
      )}
    </div>
  );
};

export default function EnterpriseJoin() {
  const { code } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [token, setToken] = useState(() => (code ? localStorage.getItem(tokenKey(code.toUpperCase())) : null));
  const [nick, setNick] = useState("");
  const [busy, setBusy] = useState(false);
  const reset = useCallback(() => { localStorage.removeItem(tokenKey((code || "").toUpperCase())); setToken(null); }, [code]);

  useEffect(() => {
    document.title = "Join event | WasntMe.Party";
    if (!code) return;
    api.get(`/enterprise/join/${code}`).then((r) => setInfo(r.data)).catch((e) => setErr(errText(e, "Event not found.")));
  }, [code]);

  const join = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post(`/enterprise/join/${code}`, { nickname: nick });
      localStorage.setItem(tokenKey(code.toUpperCase()), r.data.participant_token);
      setToken(r.data.participant_token);
    } catch (e2) { toast.error(errText(e2)); } finally { setBusy(false); }
  };

  if (!code) return <EntShell bare><CodeEntry /></EntShell>;
  if (token) return <EntShell bare><Play token={token} onReset={reset} /></EntShell>;
  return (
    <EntShell bare>
      <div className="max-w-sm mx-auto px-6 py-20">
        {err ? <p role="alert" className="font-semibold text-[#7a1010]" data-testid="join-error">{err}</p> : !info ? <p aria-busy="true">Loading…</p> : (
          <form onSubmit={join} data-testid="join-form">
            <div className="text-xs font-semibold tracking-[0.18em] uppercase" style={{ color: info.brand_hex || "#7a1010" }}>{info.org_name}{info.department ? ` · ${info.department}` : ""}</div>
            <h1 className="font-display text-4xl mt-2" data-testid="join-program">{info.program_name}</h1>
            <p className="mt-2 text-[15px] text-[#3b3530]">{info.tagline}</p>
            {info.joinable ? (
              <>
                <div className="mt-8"><Field id="nick" label="Your first name or a nickname" hint="That's all we need. No email or account required.">
                  <input id="nick" className={inputCls} maxLength={30} required value={nick} onChange={(e) => setNick(e.target.value)} autoComplete="nickname" data-testid="join-nickname" />
                </Field></div>
                <Btn type="submit" className="w-full mt-4" disabled={busy || !nick.trim()} data-testid="join-submit">Join the experience</Btn>
              </>
            ) : <p className="mt-6 font-semibold" data-testid="join-closed">This event has ended.</p>}
          </form>
        )}
      </div>
    </EntShell>
  );
}
